import { TICKET_PHOTO_MAX_BYTES } from './constants';

// Client-side ticket photo compression (spec §5):
//   • resize the longest edge down to 1600px (never upscales)
//   • re-encode as JPEG at quality 0.8
//   • re-encoding through a canvas also strips EXIF (incl. GPS) metadata
//
// Resolves with a File ready to upload, or rejects with a user-facing
// message (e.g. when the result is still larger than the 5MB ceiling).
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.8;

export default function compressTicketPhoto(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject('No photo selected.');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const { width, height } = img;
      const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
      const targetW = Math.round(width * scale);
      const targetH = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetW, targetH);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject('Could not process the photo, please retake.');
            return;
          }
          if (blob.size > TICKET_PHOTO_MAX_BYTES) {
            reject('Photo too large, please retake.');
            return;
          }
          const compressed = new File([blob], buildFileName(file.name), {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(compressed);
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject('Could not read the photo, please retake.');
    };

    img.src = objectUrl;
  });
}

// Normalise the extension to .jpg since we always re-encode to JPEG
// (covers HEIC/PNG sources being converted on upload — spec §5).
function buildFileName(originalName) {
  const base = (originalName || 'ticket-photo').replace(/\.[^/.]+$/, '');
  return `${base}.jpg`;
}
